import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { cn } from '../lib/cn';
import { useToastStore, type ToastKind } from '../stores/toastStore';

const TONE: Record<ToastKind, { wrapper: string; icon: typeof Info }> = {
  success: { wrapper: 'bg-surface border-line text-ink', icon: CheckCircle2 },
  error: { wrapper: 'bg-risk-soft border-risk text-risk', icon: AlertCircle },
  info: { wrapper: 'bg-accent-soft border-accent text-ink', icon: Info },
};

const ICON_COLOR: Record<ToastKind, string> = {
  success: 'text-ok',
  error: 'text-risk',
  info: 'text-accent',
};

export function ToastHost() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  if (toasts.length === 0) return null;

  return (
    // aria-live: ekran okuyucu bildirimi duyurur; odağı çalmaz.
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed right-6 top-24 z-[60] flex w-full max-w-sm flex-col gap-2"
    >
      {toasts.map((toast) => {
        const tone = TONE[toast.kind];
        const Icon = tone.icon;

        return (
          <div
            key={toast.id}
            role={toast.kind === 'error' ? 'alert' : 'status'}
            className={cn(
              'pointer-events-auto flex items-start gap-3 rounded-sm border p-4 shadow-xl',
              'animate-in slide-in-from-top-2 fade-in duration-300 motion-reduce:animate-none',
              tone.wrapper,
            )}
          >
            <Icon aria-hidden className={cn('mt-0.5 h-5 w-5 shrink-0', ICON_COLOR[toast.kind])} />
            <div className="flex-1">
              <p className="text-xs font-semibold leading-relaxed">{toast.text}</p>
              {toast.action && (
                /* Eylem, metnin ALTINDA ve alt çizgili: bildirimin kendisi bir
                   yüzey olduğu için ikinci bir dolu buton, kapatma ikonuyla
                   birlikte üç tıklanabilir öğe demek olurdu. */
                <button
                  type="button"
                  onClick={() => {
                    toast.action?.onClick();
                    dismiss(toast.id);
                  }}
                  className="mt-2 rounded-xs text-xs font-bold underline underline-offset-2 transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
                  data-testid="toast-action"
                >
                  {toast.action.label}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              aria-label="Bildirimi kapat"
              className="shrink-0 rounded-xs p-0.5 opacity-40 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
